import { useMemo } from 'react';

import { useProductsAuthFilters } from '@api/catalog';
import { useProductsFilters } from '@api/catalog/product';
import { getInStockFilter } from '@api/catalog/product/catalogSearchParams';

import { useAuth } from '@context/auth';

import { useCityStore } from '@store/city';

import { fromRoubleToKopecks } from '@scripts/helpers';
import { useFiltersHelper } from '@scripts/hooks';

export const useCatalogFilters = (
    filters: Record<string, unknown> | null,
    {
        enabled = true,
        enabledAuth = true,
        useUrlFilters = true,
        keepPreviousData = false,
    }: { enabled?: boolean; enabledAuth?: boolean; useUrlFilters?: boolean; keepPreviousData?: boolean } = {}
) => {
    const { isAuthUser } = useAuth();

    const { initialValues: urlFilters } = useFiltersHelper({});

    const currentCity = useCityStore(({ currentCity }) => currentCity);

    const product_ids = urlFilters.product_ids;

    delete urlFilters.product_ids;
    delete urlFilters.category;
    delete urlFilters.categories;
    delete urlFilters.page;

    const price_gte = urlFilters?.filters?.price ? fromRoubleToKopecks(urlFilters.filters.price[0]) : null;
    const price_lte = urlFilters?.filters?.price ? fromRoubleToKopecks(urlFilters.filters.price[1]) : null;

    delete urlFilters?.filters?.price;

    const isPreOrder = urlFilters?.filters?.isPreOrder;
    delete urlFilters?.filters?.isPreOrder;

    const isProfitable = urlFilters?.filters?.is_profitable;
    delete urlFilters?.filters?.is_profitable;

    const isHit = urlFilters?.filters?.is_hit;
    delete urlFilters?.filters?.is_hit;

    const preparedFilters = useUrlFilters
        ? {
              ...urlFilters?.filters,
              ...(price_gte !== null && { price_gte }),
              ...(price_lte !== null && { price_lte }),
              ...(isPreOrder === false && getInStockFilter()),
              ...(isProfitable && { is_profitable: true }),
              ...(isHit && { is_hit: true }),
          }
        : {};

    const finalFilters =
        filters !== null
            ? {
                  ...filters,
                  ...(product_ids?.length > 0 && { id: product_ids }),
                  ...(useUrlFilters && preparedFilters),
              }
            : null;

    const {
        data: filtersList,
        isLoading: isFiltersLoading,
        isFetching: isFilterFetching,
        isPending: isFiltersPending,
        fetchStatus: filtersFetchStatus,
        error: filtersError,
    } = useProductsFilters(
        {
            ...(currentCity?.region_guid && {
                customer_region_guid: currentCity.region_guid,
            }),
            ...(finalFilters !== null && {
                filter: finalFilters,
            }),
        },
        enabled,
        keepPreviousData
    );
    const isIdleFilters = isFiltersPending && filtersFetchStatus === 'idle';

    const {
        data: filtersAuthList,
        isLoading: isFiltersAuthLoading,
        isFetching: isFiltersAuthFetching,
        isPending: isFiltersAuthPending,
        fetchStatus: filtersAuthFetchStatus,
        error: filtersAuthError,
    } = useProductsAuthFilters(
        {
            ...(currentCity?.region_guid && {
                customer_region_guid: currentCity.region_guid,
            }),
            ...(finalFilters !== null && {
                filter: finalFilters,
            }),
        },
        enabledAuth,
        keepPreviousData
    );
    const isIdleFiltersAuth = isFiltersAuthPending && filtersAuthFetchStatus === 'idle';

    const filtersListData = useMemo(
        () => (isAuthUser ? filtersAuthList?.data : filtersList?.data),
        [isAuthUser, filtersList, filtersAuthList]
    );

    const error = useMemo(
        () => (isAuthUser ? filtersAuthError : filtersError),
        [isAuthUser, filtersError, filtersAuthError]
    );

    return {
        filtersListData,
        isFiltersLoading,
        isIdleFilters,
        isFiltersAuthLoading,
        isIdleFiltersAuth,
        preparedFilters,
        isFilterFetching,
        isFiltersAuthFetching,
        error,
    };
};
